import { DEFAULT_ASPECT_RATIO } from "../constants";

const getDefaultImage = (serviceId?: string, format?: string) =>
  serviceId ? `${serviceId}.${format}` : "";

const desiredImageVersion = (version: string | undefined, name: string) =>
  `${version ? "/v" + version : ""}/${name}`;

const getTransformations = ({
  aspectRatio,
  format,
  opacity,
  serviceId
}: {
  aspectRatio: string;
  format?: string;
  opacity?: number;
  serviceId?: string;
}): string => {
  const [aspectRatioWidth, aspectRatioHeight] = aspectRatio.split(":");
  const transformations = [
    "f_auto",
    "fl_lossy",
    "q_auto",
    "dpr_auto",
    "w_auto",
    "c_fill",
    `ar_${
      Number.isInteger(+aspectRatioWidth)
        ? aspectRatio
        : +aspectRatioWidth / +aspectRatioHeight
    }`
  ];

  if (serviceId) {
    transformations.push(`d_${getDefaultImage(serviceId, format)}`);
  }

  if (opacity) {
    transformations.push(`o_${opacity}`);
  }

  return transformations.join();
};

export const imageUrl = ({
  aspectRatio = DEFAULT_ASPECT_RATIO.LANDSCAPE,
  format,
  id,
  opacity,
  serviceId,
  version
}: {
  aspectRatio?: string;
  format: string;
  id: string;
  opacity?: number;
  serviceId?: string;
  version?: string;
}): string => {
  const name = id ? `${id}.${format}` : getDefaultImage(serviceId, format);
  if (!name) return "";

  return `${__IMAGES_API_URL}${getTransformations({
    aspectRatio,
    format,
    opacity,
    serviceId
  })}${desiredImageVersion(version, name)}`;
};

/*
 * Partially based on:
 *
 * Cloudinary's jQuery library - v1.0.19
 * Copyright Cloudinary
 * see https://github.com/cloudinary/cloudinary_js
 */

const getClosestHighestSupportedDpr = () => {
  const devicePixelRatio = window.devicePixelRatio || 1;
  const supportedDevicePixelRatios = [0.75, 1.0, 1.3, 1.5, 2.0, 3.0]; // Supported DPR
  let i = supportedDevicePixelRatios.length - 2;
  while (i >= 0 && supportedDevicePixelRatios[i] >= devicePixelRatio) {
    i--;
  }
  return supportedDevicePixelRatios[i + 1];
};

const MAX_WIDTH = 960;
const STEP = 80;

export const scaledUrl = (image: HTMLElement, imgUrl = ""): Promise<string> =>
  new Promise(resolve => {
    const observer = new IntersectionObserver(nodes => {
      observer.disconnect();
      const supportedDevicePixelRatio = getClosestHighestSupportedDpr();
      const scaledMaxWidth = Math.ceil(MAX_WIDTH / supportedDevicePixelRatio);
      const maxRequestedWidth = Math.min(
        STEP * Math.ceil(nodes[0].boundingClientRect.width / STEP),
        scaledMaxWidth
      ); // Stop point

      resolve(
        imgUrl
          .replace(/\bw_auto\b/g, "w_" + maxRequestedWidth)
          .replace(/\bdpr_(1\.0|auto)\b/g, "dpr_" + supportedDevicePixelRatio)
      );
    });

    observer.observe(image.parentNode as HTMLElement);
  });
